/**
 * Admin Dashboard Component
 * Shows overview stats and quick actions
 */

import { useEffect, useState } from 'react';
import { LoadingSpinner } from '../ui';

interface AdminStats {
  users: {
    total: number;
    admins: number;
    banned: number;
    newToday: number;
  };
  simulation: {
    state: string;
    uptime: number;
    creatures: number;
    food: number;
    totalSpawns: number;
  };
  connections: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/stats`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <LoadingSpinner text="Cargando estadisticas..." />
      </div>
    );
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  if (!stats) return null;

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="admin-dashboard">
      <h2>Panel Principal</h2>

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <h3>Usuarios Totales</h3>
          <span className="value">{stats.users.total}</span>
          <span className="subtitle">{stats.users.newToday} nuevos hoy</span>
        </div>

        <div className="admin-stat-card">
          <h3>Administradores</h3>
          <span className="value">{stats.users.admins}</span>
        </div>

        <div className="admin-stat-card">
          <h3>Usuarios Baneados</h3>
          <span className="value">{stats.users.banned}</span>
        </div>

        <div className="admin-stat-card">
          <h3>Conexiones Activas</h3>
          <span className="value">{stats.connections}</span>
        </div>

        <div className="admin-stat-card">
          <h3>Estado Simulación</h3>
          <span className="value">{stats.simulation.state}</span>
          <span className="subtitle">Uptime: {formatUptime(stats.simulation.uptime)}</span>
        </div>

        <div className="admin-stat-card">
          <h3>Criaturas Activas</h3>
          <span className="value">{stats.simulation.creatures}</span>
        </div>

        <div className="admin-stat-card">
          <h3>Comida Disponible</h3>
          <span className="value">{stats.simulation.food}</span>
        </div>

        <div className="admin-stat-card">
          <h3>Total Generados</h3>
          <span className="value">{stats.simulation.totalSpawns}</span>
        </div>
      </div>
    </div>
  );
}
